// SAM-1095 / SAM-1096 — full-page article editor (/settings/articles/new, /settings/articles/:articleId), mock C3.
// Replaces ArticleDetailDrawer. Form on the left, live "as residents see it" phone on the right (SAM-164 pattern).
// The Preview page gets the unsaved form through router state, like the event editor.
import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Eye, Info, Trash2 } from 'lucide-react';
import {
  useArticles, useAuthors, useArticleTypes, useCreateArticle, useUpdateArticle, useDeleteArticle, authorDisplayName,
} from '../../hooks/useArticles';
import type { Article } from '../../hooks/useArticles';
import { useUserTypes } from '../../hooks/useUserTypes';
import { useBusinesses } from '../../hooks/useBusinessAccessType';
import { useTenantBranding } from '../../hooks/useTenantBranding';
import { useFdCan } from '../../hooks/useFdCan';
import { useWhiteBorderCheck } from '../../hooks/useWhiteBorderCheck';
import { useT } from '../../i18n/admin';
import { SEMICOLON_MESSAGE } from '../../utils/crudText';
import { S3ImageUpload } from '../../components/S3ImageUpload';
import { DateField } from '../../components/ui/DateTimeField';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { BilingualPair } from '../../components/cms/BilingualPair';
import { CmsStateBadge } from '../../components/cms/CmsStateBadge';
import { ArticleHomeCard, ArticleScreen, PhoneFrame } from '../../components/cms/AppPreviews';
import {
  Card, Chip, Field, PageHead, Segmented, btnGhost, btnPrimary, inputClass, primaryStyle, selectClass,
} from '../../components/cms/EditorParts';
import { cmsState, todayInBusinessClock } from '../../utils/cmsState';
import {
  EMPTY_ARTICLE_FORM, addDays, articleMissing, articlePayload, articleToForm, defaultAuthorId, type ArticleForm, type LinkAction,
} from './cmsForms';

export interface ArticleDraftState { draft?: ArticleForm }

const createdId = (res: unknown): number | undefined => {
  const d = (res as { data?: unknown } | undefined)?.data;
  const first = Array.isArray(d) ? d[0] : d;
  const id = (first as { id?: number } | undefined)?.id;
  return typeof id === 'number' ? id : undefined;
};

const FIELD_LABEL: Record<string, string> = {
  title_en: 'Title (English)', title_es: 'Title (Español)', content_en: 'Content (English)', content_es: 'Content (Español)',
  short_description_en: 'Short text (English)', short_description_es: 'Short text (Español)',
};

export const ArticleEditorPage: React.FC = () => {
  const { articleId } = useParams<{ articleId?: string }>();
  const location = useLocation();
  const isCreate = !articleId || articleId === 'new';
  const { articles, isLoading } = useArticles();
  const restored = (location.state as ArticleDraftState | null)?.draft;
  const article = isCreate ? undefined : articles.find((a) => String(a.id) === articleId);

  if (!isCreate && isLoading) return <div className="p-8 text-sm text-gray-500">Loading…</div>;
  if (!isCreate && !article) return <NotFound />;
  // The editor starts from the loaded record, so it mounts only once that is there.
  return <ArticleEditor key={articleId ?? 'new'} article={article} restored={restored} />;
};

const NotFound: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div className="p-8 text-sm text-gray-600">
      <p className="mt-0">That article does not exist (or it was deleted).</p>
      <button type="button" className={btnGhost} onClick={() => navigate('/settings/articles')}>Back to Articles</button>
    </div>
  );
};

const LINK_OPTIONS: { id: LinkAction; text: string }[] = [
  { id: '', text: 'No button' },
  { id: 'url', text: 'Website link' },
  { id: 'business', text: 'A business' },
  { id: 'article', text: 'Another article' },
];

const ArticleEditor: React.FC<{ article?: Article; restored?: ArticleForm }> = ({ article, restored }) => {
  const navigate = useNavigate();
  const { fmt } = useT();
  const { articles } = useArticles();
  const { authors } = useAuthors();
  const { articleTypes } = useArticleTypes();
  const { userTypes } = useUserTypes();
  const { data: businessesData } = useBusinesses();
  const { tenantName } = useTenantBranding();
  const createArticle = useCreateArticle();
  const updateArticle = useUpdateArticle();
  const deleteArticle = useDeleteArticle();
  const canEdit = useFdCan('cms', 'edit');
  const whiteBorder = useWhiteBorderCheck();
  const isCreate = !article;

  const initial = useMemo<ArticleForm>(() => {
    if (restored) return restored;
    if (article) return articleToForm(article);
    return { ...EMPTY_ARTICLE_FORM, author_id: defaultAuthorId(authors, tenantName) };
    // Defaults are taken once, when the editor opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [form, setForm] = useState<ArticleForm>(initial);
  const [baseline, setBaseline] = useState<ArticleForm>(article ? articleToForm(article) : EMPTY_ARTICLE_FORM);
  const [previewLang, setPreviewLang] = useState<'en' | 'es'>('en');
  const [previewView, setPreviewView] = useState<'home' | 'article'>('article');
  // What the last Save / Publish click tried, so the errors that show are the ones for that action.
  const [attempt, setAttempt] = useState<'draft' | 'published' | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // A new article gets the brand author once the authors arrive, unless one was already chosen.
  React.useEffect(() => {
    if (isCreate && !form.author_id && authors.length) {
      const id = defaultAuthorId(authors, tenantName);
      if (id) setForm((f) => (f.author_id ? f : { ...f, author_id: id }));
    }
  }, [authors, tenantName, isCreate, form.author_id]);

  const set = <K extends keyof ArticleForm>(k: K, v: ArticleForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const today = todayInBusinessClock();
  const isDirty = JSON.stringify(form) !== JSON.stringify(baseline);
  const state = cmsState(form, today);
  const saving = createArticle.isPending || updateArticle.isPending;
  const missing = (publishing: boolean) => articleMissing(form, publishing);
  const err = (field: string) => (attempt ? missing(attempt === 'published').find((m) => m.field === field)?.message : undefined);
  const semicolonFields = (attempt ? missing(attempt === 'published') : []).filter((m) => m.message === SEMICOLON_MESSAGE).map((m) => FIELD_LABEL[m.field] ?? m.field);

  const businesses = useMemo(() => {
    const list = Array.isArray(businessesData) ? businessesData : (businessesData as { data?: unknown[] } | undefined)?.data || [];
    return [...(list as { id: string; name: string }[])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [businessesData]);

  const save = async (status: 'draft' | 'published', next: ArticleForm = form) => {
    if (articleMissing(next, status === 'published').length) { setAttempt(status); return; }
    const data = articlePayload(next, status);
    try {
      if (isCreate) {
        const res = await createArticle.mutateAsync(data);
        const id = createdId(res);
        navigate(id ? `/settings/articles/${id}` : '/settings/articles', { replace: true, state: null });
      } else {
        await updateArticle.mutateAsync({ id: article!.id!, data });
        const saved = { ...next, status };
        setForm(saved);
        setBaseline(saved);
      }
    } catch {
      // The mutation hooks already show the error toast.
    }
  };

  const back = () => {
    if (isDirty && !window.confirm('Discard your unsaved changes?')) return;
    navigate('/settings/articles');
  };
  const openPreview = () => navigate(`/settings/articles/${article?.id ?? 'new'}/preview`, { state: { draft: form } satisfies ArticleDraftState });

  const authorName = authors.find((a) => a.id === form.author_id);
  const articleData = (lang: 'en' | 'es') => ({
    title: (lang === 'en' ? form.title_en : form.title_es) || (lang === 'en' ? 'Title' : 'Título'),
    content: lang === 'en' ? form.content_en : form.content_es,
    author: authorName ? authorDisplayName(authorName) : '',
    imageKey: form.art_img_url,
  });

  const published = form.status === 'published';
  const expired = state === 'expired';
  const toggleUserType = (id: number) =>
    set('user_types_ids', form.user_types_ids.includes(id) ? form.user_types_ids.filter((x) => x !== id) : [...form.user_types_ids, id]);

  const extend = (to: string) => void save('published', { ...form, expiration_date: to });

  return (
    <div className="flex h-full flex-col bg-gray-50 text-gray-900">
      <PageHead
        back="Articles"
        onBack={back}
        title={form.title_en || (isCreate ? 'New article' : 'Article')}
        badge={<CmsStateBadge state={state} />}
        actions={
          <>
            <button type="button" className={btnGhost} onClick={openPreview} style={{ color: 'var(--tenant-primary, #0B5C4D)', borderColor: 'var(--tenant-primary, #0B5C4D)' }}>
              <Eye size={16} aria-hidden /> Preview in app
            </button>
            {canEdit && (published ? (
              <>
                <button type="button" className={btnGhost} disabled={saving} onClick={() => void save('draft')}>Move to draft</button>
                <button type="button" className={btnPrimary} style={primaryStyle} disabled={saving || !isDirty} onClick={() => void save('published')}>Save changes</button>
              </>
            ) : (
              <>
                <button type="button" className={btnGhost} disabled={saving} onClick={() => void save('draft')}>Save draft</button>
                <button type="button" className={btnPrimary} style={primaryStyle} disabled={saving} onClick={() => void save('published')}>Publish</button>
              </>
            ))}
          </>
        }
      />

      {expired && (
        <div className="mx-8 mt-4 flex flex-wrap items-center gap-3 rounded-lg bg-gray-100 px-4 py-3 text-sm text-gray-800" role="status">
          <span><b>Expired on {fmt.date(form.expiration_date)}.</b> Residents stopped seeing it that day.</span>
          <span className="flex-1" />
          {canEdit && (
            <>
              <button type="button" className={btnGhost} onClick={() => extend(addDays(today, 7))}>Extend 7 days</button>
              <button type="button" className={btnGhost} onClick={() => extend(addDays(today, 30))}>Extend 30 days</button>
            </>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="flex min-h-full items-stretch">
        <fieldset disabled={!canEdit} className="m-0 flex min-w-0 flex-1 flex-col gap-4 border-0 px-8 pb-12 pt-6">
          <Card title="Content" subtitle="Write in English; Español is translated for you">
            <BilingualPair
              fields={[
                { base: 'title', label: 'Title', error: err('title_en') },
                { base: 'content', label: 'Content', multiline: true, minHeight: 170, error: err('content_en') },
              ]}
              values={form as unknown as Record<string, string>}
              onChange={(k, v) => setForm((f) => ({ ...f, [k]: v }))}
              disabled={!canEdit}
            />
            {err('title_es') && <span className="text-xs text-red-600">{err('title_es')}</span>}
            {semicolonFields.length > 0 && (
              <span role="alert" className="text-xs text-red-600">{SEMICOLON_MESSAGE} In: {semicolonFields.join(', ')}.</span>
            )}
          </Card>

          <Card title="Image" subtitle="Shown on the Home card and at the top of the article">
            <S3ImageUpload label="" value={form.art_img_url} onChange={(key) => set('art_img_url', key)} onFileChosen={whiteBorder.onFileChosen} previewHeight="112px" objectFit="cover" />
            {whiteBorder.warn && (
              <div role="status" className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
                This image has a white or almost white border. It looks washed out in the app: crop the border or pick another photo.
              </div>
            )}
            <span className="text-xs text-gray-500">Wide photo · 16:9 works best</span>
          </Card>

          <Card title="Publishing">
            <div className="flex items-center gap-3">
              <span className="w-[60px] text-xs font-semibold text-gray-500">Status</span>
              <Segmented
                label="Status"
                value={form.status}
                options={[{ id: 'draft', text: 'Draft' }, { id: 'published', text: 'Published' }]}
                onChange={(v) => set('status', v as 'draft' | 'published')}
                disabled={!canEdit}
              />
              <span className="flex-1" />
              <span className="text-[13px] text-gray-500">{form.status === 'draft' ? 'Not visible to residents' : state === 'scheduled' ? 'Goes live on its Publishes date' : state === 'expired' ? 'Past its Expires date' : 'Visible to residents'}</span>
            </div>
            <div className="flex gap-3">
              <Field label="Publishes" hint={undefined} error={err('publish_date')}>
                <DateField ariaLabel="Publishes" value={form.publish_date} onChange={(v) => set('publish_date', v)} invalid={!!err('publish_date')} />
              </Field>
              <Field label="Expires" error={err('expiration_date')}>
                <DateField ariaLabel="Expires" value={form.expiration_date} min={form.publish_date || undefined} onChange={(v) => set('expiration_date', v)} invalid={!!err('expiration_date')} />
              </Field>
            </div>
            {form.publish_date && form.expiration_date && (
              <div className="flex items-start gap-2 rounded-lg bg-gray-100 px-3 py-2.5 text-[13px] text-gray-700">
                <Info size={16} className="shrink-0 text-gray-500" aria-hidden />
                <span>When you publish, it shows in the app from {fmt.date(form.publish_date)} to {fmt.date(form.expiration_date)} and hides itself after that day. Nothing to clean up.</span>
              </div>
            )}
          </Card>

          <Card title="Details">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Author" required htmlFor="cms-author" hint="Required. Default is the tenant brand. Shown under the title in the app." error={err('author_id')}>
                <select id="cms-author" className={selectClass} value={form.author_id || ''} onChange={(e) => set('author_id', Number(e.target.value))}>
                  <option value="">Select an author</option>
                  {authors.map((a) => <option key={a.id} value={a.id}>{authorDisplayName(a)}</option>)}
                </select>
              </Field>
              <Field label="Type" htmlFor="cms-type" error={err('article_type_id')}>
                <select id="cms-type" className={selectClass} value={form.article_type_id || ''} onChange={(e) => set('article_type_id', Number(e.target.value))}>
                  <option value="">Select a type</option>
                  {articleTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </Field>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-gray-500">Who sees it</span>
              <div className="flex flex-wrap gap-1.5">
                {userTypes.map((u) => (
                  <Chip key={u.id} on={form.user_types_ids.includes(u.id)} onClick={() => toggleUserType(u.id)} disabled={!canEdit}>{u.name}</Chip>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Button in the article" htmlFor="cms-link-action">
                <select id="cms-link-action" className={selectClass} value={form.link_action} onChange={(e) => setForm((f) => ({ ...f, link_action: e.target.value as LinkAction, link_id: '' }))}>
                  {LINK_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.text}</option>)}
                </select>
              </Field>
              {form.link_action === 'url' && (
                <Field label="Link" htmlFor="cms-link-id">
                  <input id="cms-link-id" className={inputClass} value={form.link_id} placeholder="https://" onChange={(e) => set('link_id', e.target.value)} />
                </Field>
              )}
              {form.link_action === 'business' && (
                <Field label="Business" htmlFor="cms-link-id">
                  <select id="cms-link-id" className={selectClass} value={form.link_id} onChange={(e) => set('link_id', e.target.value)}>
                    <option value="">Select a business</option>
                    {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </Field>
              )}
              {form.link_action === 'article' && (
                <Field label="Article" htmlFor="cms-link-id">
                  <select id="cms-link-id" className={selectClass} value={form.link_id} onChange={(e) => set('link_id', e.target.value)}>
                    <option value="">Select an article</option>
                    {articles.filter((a) => a.id !== article?.id).map((a) => <option key={a.id} value={a.id}>{a.title_en}</option>)}
                  </select>
                </Field>
              )}
            </div>
          </Card>

          {canEdit && !isCreate && (
            <div>
              <button type="button" className={`${btnGhost} text-red-700`} onClick={() => setConfirmDelete(true)}>
                <Trash2 size={14} aria-hidden /> Delete article
              </button>
            </div>
          )}
        </fieldset>

        <aside className="flex w-[380px] shrink-0 flex-col items-center gap-3.5 border-l border-gray-200 bg-gray-100 px-4 py-6" aria-label="Preview">
          <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Preview · as residents see it</div>
          <div className="flex flex-wrap justify-center gap-2">
            <Segmented label="Language" value={previewLang} options={[{ id: 'en', text: 'English' }, { id: 'es', text: 'Español' }]} onChange={(v) => setPreviewLang(v as 'en' | 'es')} />
            <Segmented label="Screen" value={previewView} options={[{ id: 'home', text: 'Home card' }, { id: 'article', text: 'Article' }]} onChange={(v) => setPreviewView(v as 'home' | 'article')} />
          </div>
          <PhoneFrame height={700} label={`Article preview, ${previewLang === 'en' ? 'English' : 'Español'}`}>
            {previewView === 'home' ? <ArticleHomeCard article={articleData(previewLang)} /> : <ArticleScreen article={articleData(previewLang)} />}
          </PhoneFrame>
          <div className="max-w-[300px] text-center text-xs text-gray-500">Updates as you type. Same layout as the app.</div>
        </aside>
      </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => deleteArticle.mutate(article!.id!, { onSuccess: () => navigate('/settings/articles') })}
        title="Delete article"
        message="This is permanent and cannot be undone."
        confirmText="Delete"
        isLoading={deleteArticle.isPending}
      />
    </div>
  );
};
